import React from 'react'
import { useHistory } from 'react-router-dom'
import { useModal, Flex, Text } from 'vvs-uikit'
import { useCleanMutation } from 'vvs-gotchi-graphql'
import useToast from 'hooks/useToast'
import { useGotchiAuth } from 'state/vvsgotchi/hooks/useAuth'
import useGotchiOwner from 'state/vvsgotchi/hooks/useGotchiOwner'
import useActiveWeb3React from 'hooks/useActiveWeb3React'
import { useTranslation } from 'contexts/Localization'
import { toastTypes } from 'components/Toast'
import { CloseButton } from 'components/CloseButton'
import PixelatedImage from 'views/VvsGotchi/components/PixelatedImage'
import ActionButton from 'views/VvsGotchi/components/ActionButton'
import { RunningModal } from 'views/VvsGotchi/components/Modal'
import { useCountdownObj } from 'hooks/useCountdown'
import { Path } from 'config/constants/paths'
import waitPromise from 'utils/waitPromise'
import historyBackOrReplace from 'utils/historyBackOrReplace'
import { useVvsGotchiAudioModeManager } from 'state/vvsgotchi/hooks/useConfigManager'
import * as ga from 'utils/googleAnalyticsHelper'
import { DarkPageContainer } from '../styles'
import sound from '../../../assets/clean_success.mp3'

const cleanSound = new Audio(sound)

const CleanMole: React.FC = () => {
  const { t } = useTranslation()
  const history = useHistory()
  const { team, loading } = useGotchiOwner()

  return (
    <DarkPageContainer popup>
      <Flex justifyContent="space-between" mx={3} mt="24px" mb={4}>
        <Text fontSize="20px" fontWeight="600" color="white">
          {t('Clean')}
        </Text>
        <CloseButton onClick={() => historyBackOrReplace(history, Path.VVS_GOTCHI_TEAM)} />
      </Flex>
      {!(team?.length > 0) ? (
        <>
          <Flex flexDirection="column" justifyContent="center" alignItems="center" my={5} mx={4}>
            {loading ? (
              <>
                <Text fontSize="20px" fontWeight="600" color="white">
                  {t('Loading...')}
                </Text>
              </>
            ) : (
              <>
                <Text fontSize="20px" fontWeight="600" color="white">
                  {t('( ˘︹˘ )')}
                </Text>
                <Text fontSize="20px" fontWeight="600" color="white">
                  {t('No Moles to Clean')}
                </Text>
                <Text textAlign="center" color="white">
                  {t('Add Miner Moles to the Team but make sure to take care of them!')}
                </Text>
              </>
            )}
          </Flex>
          <ActionButton mt={4} disabled={loading} onClick={() => history.replace(Path.VVS_GOTCHI_TEAM_SELECT)}>
            {t('Add Miner Moles')}
          </ActionButton>
        </>
      ) : (
        <TimeToCleanMole />
      )}
    </DarkPageContainer>
  )
}

export default CleanMole

const TimeToCleanMole = () => {
  const { t } = useTranslation()
  const { toastVVSGotchi } = useToast()
  const history = useHistory()
  const [, ensureAuth] = useGotchiAuth()
  const { account } = useActiveWeb3React()
  const { owner, refresh } = useGotchiOwner()
  const [clean] = useCleanMutation()
  const { sePlay } = useVvsGotchiAudioModeManager()
  const [openCleaningModal, closeCleaningModal] = useModal(
    <RunningModal imageSrc="/images/vvsGotchi/clean.png">{t('Cleaning...')}</RunningModal>,
    false,
    false,
    'vvsgotchiCleaning',
  )

  const isClean = owner?.isClean || false
  return (
    <>
      <Flex flexDirection="column" justifyContent="center" alignItems="center" my={4} mx={4}>
        {isClean ? (
          <>
            <PixelatedImage src="/images/vvsGotchi/toilet.png" mb={2} />
            <Text fontSize="20px" fontWeight="600" color="white">
              {t('Team is clean ( ＾0＾)')}
            </Text>
            <Text textAlign="center" color="white">
              {t('No cleaning needed right now')}
            </Text>
          </>
        ) : (
          <>
            <PixelatedImage src={isClean ? '/images/vvsGotchi/toilet.png' : '/images/vvsGotchi/clean.png'} mb={2} />
            <Text fontSize="20px" fontWeight="600" color="white">
              {t('Time to clean Poop')}
            </Text>
            {owner?.nextDirtyDeclineTime && <CleanCountDown time={owner.nextDirtyDeclineTime} />}
            <Text textAlign="center" color="white">
              {t(
                "Make sure to clean before the timer runs out! You'll lose -2 health points every 8 hours if your Moles are not cleaned.",
              )}
            </Text>
          </>
        )}
      </Flex>
      <ActionButton
        mt={4}
        disabled={isClean}
        onClick={async () => {
          try {
            await ensureAuth()
            openCleaningModal()
            ga.gotchiCleanAction()
            const { data: cleanResultData, errors: cleanErrors } = await clean({
              variables: {
                address: account,
              },
            })

            if (cleanErrors) throw cleanErrors
            await Promise.all([
              refresh(),
              waitPromise(2000), // clean mole for 2s
            ])

            if (cleanResultData.clean.hasClean) {
              if (sePlay) {
                cleanSound.play()
              }
              toastVVSGotchi(
                t('Team is clean now ( ＾0＾)'),
                toastTypes.INFO,
                <PixelatedImage src="/images/vvsGotchi/toilet.png" width="18px" />,
                t('Cleaning is successful!'),
              )
            } else {
              toastVVSGotchi(
                t('Failed to claean'),
                toastTypes.INFO,
                <PixelatedImage src="/images/vvsGotchi/toilet_gray.png" width="18px" />,
                t('Something went wrong'),
              )
            }
            historyBackOrReplace(history, Path.VVS_GOTCHI_TEAM)
          } catch (err) {
            console.error(err)
          } finally {
            closeCleaningModal()
          }
        }}
      >
        {t('Clean Now')}
      </ActionButton>
    </>
  )
}

const CleanCountDown = ({ time }: { time: string }) => {
  const { t } = useTranslation()
  const { refresh } = useGotchiOwner()
  const timeExcerptMap = useCountdownObj(time, refresh)

  return (
    <Text fontSize="20px" fontWeight="500" color="warningLight" my={2}>
      {timeExcerptMap.h > 0 ? t('%h%hr %m%m %s%s', timeExcerptMap) : t('%m%m %s%s', timeExcerptMap)}
    </Text>
  )
}
